/**
 * A recorded lesson's length, read from a video file or a stored upload.
 *
 * Never typed: the teacher is not asked for it. Linked videos are read in
 * `api/videoDuration.api.ts`, since those ask another site; this file never
 * leaves the browser.
 */

/** Saving waits on this read, so a stalled one must give up rather than hang the form. */
const READ_TIMEOUT_MS = 10_000;

/**
 * The length of a video at an address, from its own metadata, or null when the
 * browser cannot tell.
 *
 * Only the header is read (`preload = 'metadata'`), so a 500 MB video costs
 * nothing extra. Works on a lesson already uploaded too: its signed playback
 * address is a plain media source, and a `<video>` needs no CORS to report a
 * duration. Null is an ordinary answer: some browsers cannot decode MOV, and a
 * WEBM straight out of a screen recorder often reports `Infinity` because it
 * was never given a duration. The lesson is saved without a length then.
 */
export function readMediaDuration(src: string): Promise<number | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;

    let isDone = false;
    const finish = (seconds: number | null) => {
      if (isDone) return;
      isDone = true;
      window.clearTimeout(timeout);
      video.removeAttribute('src');
      video.load();
      resolve(seconds);
    };
    const timeout = window.setTimeout(() => finish(null), READ_TIMEOUT_MS);

    video.onloadedmetadata = () =>
      finish(Number.isFinite(video.duration) && video.duration > 0 ? Math.round(video.duration) : null);
    video.onerror = () => finish(null);
    video.src = src;
  });
}

/** The length of a file the teacher has just chosen, before it is uploaded. */
export async function readFileDuration(file: File): Promise<number | null> {
  const objectUrl = URL.createObjectURL(file);
  try {
    return await readMediaDuration(objectUrl);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
